import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { ShoppingBag } from "lucide-react";
import {
  computeStageStats,
  filterOrdersByStage,
  hasPresentPartnerDrivers,
  mapOrdersRpcError,
  searchOrders,
  type PartnerOrderFilter,
  type PartnerOrderRow,
} from "../../lib/partnerOrders";
import {
  useAcceptPartnerOrder,
  useDeclinePartnerOrder,
  usePartnerDrivers,
  usePartnerFallbackEnabled,
  usePartnerOrderDetail,
  usePartnerOrdersList,
  useRequestOeloxDriver,
  useUnassignPartnerDriver,
} from "../../hooks/usePartnerOrders";
import { loadPartnerUiPreferences } from "../../lib/partnerSettings";
import {
  isInDateRange,
  type PartnerDashboardDateRange,
} from "../../lib/partnerDashboardDate";
import { appIntlLocale } from "../../lib/partnerTime";
import { useI18n, type TranslationKey } from "../../i18n";
import { usePartnerDashboardDateRange } from "../../hooks/usePartnerDashboardDateRange";
import { OverviewDateControl } from "../dashboard/OverviewDateControl";
import {
  AcceptDispatchModal,
  type AcceptDispatchSuccess,
} from "./AcceptDispatchModal";
import { DeclineOrderModal } from "./DeclineOrderModal";
import { NewOrderToast } from "./NewOrderToast";
import { OrdersStepStrip } from "./OrdersStepStrip";
import { OrderCard, OrderCardSkeleton } from "./OrderCard";
import {
  OrderDetailEmpty,
  OrderDetailPanel,
  OrderDetailSkeleton,
} from "./OrderDetailPanel";

type MobilePane = "list" | "detail";
type OrderSortKey = "newest" | "oldest" | "amount";

/** Full stage names for user-facing copy (empty states, filters). */
const FILTER_STAGE_KEY: Record<
  Exclude<PartnerOrderFilter, "all">,
  TranslationKey
> = {
  pending: "orders.stages.pending",
  processing: "orders.stages.processing",
  ready: "orders.stages.ready",
  in_delivery: "orders.stages.in_delivery",
  completed: "orders.stages.completed",
  cancelled: "orders.stages.cancelled",
};

/** Active stages stay visible; finished stages respect the date range (same rule as Overview). */
function forOrdersView(
  orders: PartnerOrderRow[],
  range: PartnerDashboardDateRange
): PartnerOrderRow[] {
  return orders.filter((o) => {
    if (o.stage !== "completed" && o.stage !== "cancelled") return true;
    return isInDateRange(o.created_at, range);
  });
}

export interface LiveOrdersFlowProps {
  /** Page title in the toolbar. */
  title?: string;
  selectedId: string | null;
  onSelectedIdChange: (id: string | null) => void;
}

function sortOrders(
  orders: PartnerOrderRow[],
  key: OrderSortKey
): PartnerOrderRow[] {
  if (key === "amount") {
    return [...orders].sort((a, b) => b.total - a.total);
  }
  const dir = key === "oldest" ? 1 : -1;
  return [...orders].sort((a, b) => {
    const at = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
    return (at - bt) * dir;
  });
}

function formatOrderTime(iso: string | null, locale: string): string | null {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat(appIntlLocale(locale), {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      numberingSystem: "latn",
    }).format(new Date(iso));
  } catch {
    return null;
  }
}

/**
 * Canonical partner live-orders board — same stage model + mutations as Orders page.
 * Used by Dashboard (HQ) and Orders route.
 */
export function LiveOrdersFlow({
  title,
  selectedId,
  onSelectedIdChange,
}: LiveOrdersFlowProps) {
  const { t, direction, locale } = useI18n();
  const pageTitle = title ?? t("orders.title");

  const initialFilter = (() => {
    const focus = loadPartnerUiPreferences().defaultStageFocus;
    if (focus === "all") return "pending" as PartnerOrderFilter;
    return focus as PartnerOrderFilter;
  })();

  const [filter, setFilter] = useState<PartnerOrderFilter>(initialFilter);
  const [search, setSearch] = useState("");
  const [mobilePane, setMobilePane] = useState<MobilePane>("list");
  const [dispatchOrder, setDispatchOrder] = useState<PartnerOrderRow | null>(
    null
  );
  const [dispatchAcceptFirst, setDispatchAcceptFirst] = useState(true);
  const [declineOpen, setDeclineOpen] = useState(false);

  const searchRef = useRef<HTMLInputElement>(null);
  const listScrollRef = useRef<HTMLDivElement>(null);
  const knownIdsRef = useRef<Set<string> | null>(null);

  const { range, setToday, setCustom } = usePartnerDashboardDateRange();
  const listQuery = usePartnerOrdersList();
  const detailQuery = usePartnerOrderDetail(selectedId);
  const driversQuery = usePartnerDrivers();
  const fallbackQuery = usePartnerFallbackEnabled();

  const acceptMutation = useAcceptPartnerOrder();
  const unassignMutation = useUnassignPartnerDriver();
  const oeloxMutation = useRequestOeloxDriver();
  const declineMutation = useDeclinePartnerOrder();

  const allOrders = listQuery.data?.orders ?? [];
  const rangedOrders = useMemo(
    () => forOrdersView(allOrders, range),
    [allOrders, range]
  );
  const stats = useMemo(() => computeStageStats(rangedOrders), [rangedOrders]);
  const filtered = useMemo(() => {
    const base = searchOrders(filterOrdersByStage(rangedOrders, filter), search);
    return sortOrders(base, "newest");
  }, [rangedOrders, filter, search]);

  const selectedFromList = useMemo(
    () => allOrders.find((o) => o.id === selectedId) ?? null,
    [allOrders, selectedId]
  );
  const selectedDetail = detailQuery.data ?? null;

  const drivers = driversQuery.data ?? [];
  const fallbackEnabled = fallbackQuery.data === true;
  const showFallback =
    fallbackEnabled &&
    (!hasPresentPartnerDrivers(drivers) || drivers.length === 0);

  const selectOrder = useCallback(
    (id: string) => {
      onSelectedIdChange(id);
      setMobilePane("detail");
    },
    [onSelectedIdChange]
  );

  const clearSelection = useCallback(() => {
    onSelectedIdChange(null);
    setMobilePane("list");
  }, [onSelectedIdChange]);

  const openAcceptDispatch = useCallback(
    (order: PartnerOrderRow, acceptFirst: boolean) => {
      selectOrder(order.id);
      setDispatchAcceptFirst(acceptFirst);
      setDispatchOrder(order);
    },
    [selectOrder]
  );

  useEffect(() => {
    setMobilePane(selectedId ? "detail" : "list");
  }, [selectedId]);

  // Pending count in the window title — desktop app feel.
  useEffect(() => {
    const base = `${pageTitle} · OELOX Partner`;
    document.title = stats.pending > 0 ? `(${stats.pending}) ${base}` : base;
    return () => {
      document.title = "OELOX Partner";
    };
  }, [pageTitle, stats.pending]);

  // Newly routed orders → rich toast with a one-click jump.
  useEffect(() => {
    const orders = listQuery.data?.orders;
    if (!orders) return;
    const ids = new Set(orders.map((o) => o.id));
    const previous = knownIdsRef.current;
    knownIdsRef.current = ids;
    if (!previous) return;
    const fresh = orders.find(
      (o) => o.stage === "pending" && !previous.has(o.id)
    );
    if (!fresh) return;
    toast.custom(
      (toastItem) => (
        <NewOrderToast
          title={t("orders.toast.newOrder")}
          subtitle={`${fresh.displayNumber} · ${fresh.customer_name}`}
          actionLabel={t("orders.toast.view")}
          closeLabel={t("common.close")}
          onView={() => {
            toast.dismiss(toastItem.id);
            setFilter("pending");
            selectOrder(fresh.id);
          }}
          onClose={() => toast.dismiss(toastItem.id)}
        />
      ),
      {
        duration: 7000,
        position: direction === "rtl" ? "top-left" : "top-right",
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listQuery.data]);

  // Keyboard shortcuts — app-like navigation (/ focus, arrows, Esc).
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing =
        !!target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.isContentEditable);

      if (event.key === "/" && !typing) {
        event.preventDefault();
        searchRef.current?.focus();
        return;
      }
      if (event.key === "Escape") {
        if (typing && target === searchRef.current) {
          target.blur();
          return;
        }
        if (typing || dispatchOrder || declineOpen) return;
        if (selectedId) clearSelection();
        return;
      }
      if (typing) return;
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        if (filtered.length === 0) return;
        event.preventDefault();
        const currentIndex = filtered.findIndex((o) => o.id === selectedId);
        const nextIndex =
          currentIndex === -1
            ? 0
            : event.key === "ArrowDown"
              ? Math.min(currentIndex + 1, filtered.length - 1)
              : Math.max(currentIndex - 1, 0);
        const next = filtered[nextIndex];
        if (next) selectOrder(next.id);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    filtered,
    selectedId,
    dispatchOrder,
    declineOpen,
    selectOrder,
    clearSelection,
  ]);

  // Keep the selected card visible inside the list pane.
  useEffect(() => {
    if (!selectedId) return;
    const node = listScrollRef.current?.querySelector(
      `[data-order-id="${selectedId}"]`
    );
    node?.scrollIntoView({ block: "nearest" });
  }, [selectedId]);

  useEffect(() => {
    const first = filtered[0];
    if (selectedId || !first) return;
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 767px)").matches
    ) {
      return;
    }
    selectOrder(first.id);
  }, [filtered, selectedId, selectOrder]);

  const busy =
    acceptMutation.isPending ||
    unassignMutation.isPending ||
    declineMutation.isPending ||
    oeloxMutation.isPending;

  const handlePrimary = (order: PartnerOrderRow) => {
    if (order.stage === "pending" && order.fulfillment_type === "delivery") {
      openAcceptDispatch(order, true);
      return;
    }
    if (
      (order.stage === "ready" || order.stage === "in_delivery") &&
      order.fulfillment_type === "delivery"
    ) {
      openAcceptDispatch(order, false);
      return;
    }
    selectOrder(order.id);
  };

  const handleDispatchSuccess = (result: AcceptDispatchSuccess) => {
    toast.success(t(result.messageKey));
    if (result.filter) setFilter(result.filter);
    if (dispatchOrder) selectOrder(dispatchOrder.id);
    void listQuery.refetch();
    void detailQuery.refetch();
  };

  const handleUnassignDriver = async () => {
    const order = selectedDetail ?? selectedFromList;
    if (!order) return;
    try {
      await unassignMutation.mutateAsync({
        tenantId: order.tenant_id,
        orderId: order.id,
      });
      toast.success(t("orders.toast.cleared"));
      setFilter("ready");
      void detailQuery.refetch();
    } catch (err) {
      toast.error(t(mapOrdersRpcError(err)));
    }
  };

  const handleRequestOelox = async () => {
    const order = selectedDetail ?? selectedFromList;
    if (!order) return;
    try {
      if (!order.partner_accepted_at) {
        await acceptMutation.mutateAsync({
          tenantId: order.tenant_id,
          orderId: order.id,
        });
      }
      await oeloxMutation.mutateAsync({
        tenantId: order.tenant_id,
        orderId: order.id,
      });
      toast.success(t("orders.toast.oeloxRequested"));
      setFilter("in_delivery");
      void detailQuery.refetch();
    } catch (err) {
      toast.error(t(mapOrdersRpcError(err)));
    }
  };

  /** Phase 2: decline a routed order before accepting it — in-app modal. */
  const openDeclineModal = () => setDeclineOpen(true);

  const handleDeclineConfirm = async (reason: string) => {
    const order = selectedDetail ?? selectedFromList;
    if (!order) return;
    try {
      await declineMutation.mutateAsync({
        tenantId: order.tenant_id,
        orderId: order.id,
        reason: reason.trim() || undefined,
      });
      toast.success(t("orders.toast.declined"));
      setDeclineOpen(false);
      clearSelection();
      void listQuery.refetch();
    } catch (err) {
      toast.error(t(mapOrdersRpcError(err)));
    }
  };

  return (
    <div className="app-workspace app-workspace--orders">
      <header className="menu-page__header overview-page__header orders-head">
        <div className="min-w-0">
          <p className="menu-page__eyebrow">{t("orders.eyebrow")}</p>
          <h1 className="menu-page__title overview-page__title">
            {pageTitle}
            <span className="orders-head__count">{rangedOrders.length}</span>
          </h1>
          <p className="menu-page__subtitle overview-page__subtitle">
            {t("orders.subtitle")}
          </p>
        </div>
        <div className="menu-page__header-actions overview-page__header-actions">
          <OverviewDateControl
            range={range}
            onToday={setToday}
            onCustom={setCustom}
            compact
          />
        </div>
      </header>

      <div className="customer-master app-workspace__board">
        <section
          className={`customer-list-pane orders-workspace__list ${
            mobilePane === "list"
              ? "customer-pane--visible"
              : "customer-pane--hidden-md"
          }`}
        >
          <div className="customer-list-toolbar orders-list-head">
            <input
              ref={searchRef}
              className="orders-workspace__search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("orders.searchPlaceholder")}
              aria-label={t("orders.searchAria")}
            />
            <OrdersStepStrip
              value={filter}
              onChange={setFilter}
              stats={stats}
              allCount={rangedOrders.length}
            />
          </div>

          <div className="customer-list-scroll" ref={listScrollRef}>
            {listQuery.isLoading ? (
              <div className="orders-list-stack">
                {[1, 2, 3, 4, 5].map((i) => (
                  <OrderCardSkeleton key={i} />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="app-workspace__state">
                <ShoppingBag className="mx-auto h-8 w-8 text-[var(--text-muted)]" />
                <p className="mt-3 text-sm font-semibold text-[var(--text-secondary)]">
                  {filter === "all"
                    ? t("orders.emptyTitle")
                    : t("orders.emptyTitleStage", {
                        stage: t(FILTER_STAGE_KEY[filter]),
                      })}
                </p>
                <p className="mt-1 text-xs text-[var(--text-muted)]">
                  {t("orders.emptyHint")}
                </p>
                {allOrders.length > 0 && rangedOrders.length === 0 ? (
                  <p className="mt-1 text-xs font-semibold text-[var(--text-secondary)]">
                    {t("orders.emptyRangeHint")}
                  </p>
                ) : null}
                {filter !== "all" ? (
                  <button
                    type="button"
                    className="btn-secondary mt-3 min-h-10 px-4 text-xs"
                    onClick={() => setFilter("all")}
                  >
                    {t("orders.showAllOrders")}
                  </button>
                ) : null}
                <p className="orders-empty-listening">
                  <span className="orders-live__dot" aria-hidden />
                  {t("orders.emptyListening")}
                </p>
              </div>
            ) : (
              <div className="orders-list-stack">
                {filtered.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    selected={order.id === selectedId}
                    timestamp={formatOrderTime(order.created_at, locale)}
                    onSelect={() => selectOrder(order.id)}
                    onPrimary={() => handlePrimary(order)}
                    primaryBusy={busy && selectedId === order.id}
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        <section
          className={`customer-detail-pane orders-workspace__detail ${
            mobilePane === "detail"
              ? "customer-pane--visible"
              : "customer-pane--hidden-md"
          }`}
        >
          {detailQuery.isLoading && selectedId ? (
            <OrderDetailSkeleton />
          ) : selectedDetail ? (
            <OrderDetailPanel
              key={selectedDetail.id}
              order={selectedDetail}
              busy={busy}
              drivers={drivers}
              driversLoading={driversQuery.isLoading}
              fallbackEnabled={fallbackEnabled}
              showFallback={showFallback || drivers.length === 0}
              onClose={clearSelection}
              onPrimary={() => {
                if (
                  selectedDetail.stage === "pending" &&
                  selectedDetail.fulfillment_type === "delivery"
                ) {
                  openAcceptDispatch(selectedDetail, true);
                  return;
                }
                if (
                  (selectedDetail.stage === "ready" ||
                    selectedDetail.stage === "in_delivery") &&
                  selectedDetail.fulfillment_type === "delivery" &&
                  !selectedDetail.assigned_driver_id
                ) {
                  openAcceptDispatch(selectedDetail, false);
                }
              }}
              onChangeDriver={() => openAcceptDispatch(selectedDetail, false)}
              onDecline={
                selectedDetail.stage === "pending"
                  ? openDeclineModal
                  : undefined
              }
              onUnassignDriver={handleUnassignDriver}
              onRequestOeloxDriver={
                fallbackEnabled ? handleRequestOelox : undefined
              }
            />
          ) : (
            <OrderDetailEmpty />
          )}
        </section>
      </div>

      <AcceptDispatchModal
        open={!!dispatchOrder}
        order={dispatchOrder}
        acceptFirst={dispatchAcceptFirst}
        onClose={() => setDispatchOrder(null)}
        onSuccess={handleDispatchSuccess}
      />

      <DeclineOrderModal
        open={declineOpen}
        order={selectedDetail ?? selectedFromList}
        busy={declineMutation.isPending}
        onClose={() => setDeclineOpen(false)}
        onConfirm={handleDeclineConfirm}
      />
    </div>
  );
}
